import React, { useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";

const API_BASE =
  import.meta?.env?.VITE_API_BASE || "https://api.vyapakprint.com/api"; // change if needed

const Login = () => {
  const navigate = useNavigate();

  const [showPassword, setShowPassword] = useState(false);
  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const onChange = (e) => {
    setError("");
    setForm((p) => ({ ...p, [e.target.id]: e.target.value }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!form.email || !form.password) {
      setError("Email and password are required.");
      return;
    }

    try {
      setLoading(true);

      // ✅ Admin login API
      const { data } = await axios.post(
        `${API_BASE}/admin/login`,
        {
          email: form.email.trim(),
          password: form.password,
        },
        { headers: { "Content-Type": "application/json" } },
      );

      // ✅ Save token
      const token = data?.token;
      if (!token) {
        setError("Token not received from server.");
        return;
      }

      localStorage.setItem("adminToken", token);

      // ✅ (Optional) set default header for future requests
      axios.defaults.headers.common.Authorization = `Bearer ${token}`;

      // ✅ Redirect (change route)
      navigate("/");
    } catch (err) {
      const msg =
        err?.response?.data?.msg ||
        err?.response?.data?.message ||
        err?.message ||
        "Login failed";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-gray-100 to-gray-200 flex">
      {/* Left */}
      <div className="hidden md:flex md:w-1/2 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900" />
        <div className="absolute -top-24 -left-24 h-72 w-72 rounded-full bg-white/10 blur-2xl" />
        <div className="absolute -bottom-24 -right-24 h-72 w-72 rounded-full bg-white/10 blur-2xl" />

        <div className="relative z-10 p-12 flex flex-col justify-center text-white">
          <div className="inline-flex items-center gap-2 mb-8">
            <div className="h-10 w-10 rounded-xl bg-white/10 border border-white/15 flex items-center justify-center font-bold">
              V
            </div>
            <span className="text-sm tracking-wide text-white/80">
              vyapakprint
            </span>
          </div>

          <h1 className="text-4xl font-extrabold leading-tight">
            Welcome back 👋
          </h1>
          <p className="mt-3 text-white/80 text-lg">
            Log in to manage orders, products and customers.
          </p>

          <div className="mt-10 rounded-2xl border border-white/10 bg-white/5 p-5">
            <p className="text-sm text-white/80">
              Made with love by <span className="text-white">Soft Coders</span>
            </p>
          </div>
        </div>
      </div>

      {/* Right */}
      <div className="w-full md:w-1/2 flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-md">
          <div className="bg-white/90 backdrop-blur rounded-2xl shadow-xl border border-gray-200 p-8 sm:p-10">
            <h2 className="text-2xl font-bold text-gray-900">Admin Login</h2>
            <p className="text-sm text-gray-500 mt-1">
              Enter your credentials to continue.
            </p>

            {/* ✅ Error box */}
            {error ? (
              <div className="mt-5 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                {error}
              </div>
            ) : null}

            <form className="mt-7 space-y-5" onSubmit={onSubmit}>
              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-gray-700"
                >
                  Email
                </label>
                <input
                  type="email"
                  id="email"
                  value={form.email}
                  onChange={onChange}
                  className="mt-2 w-full rounded-xl border border-gray-300 bg-white px-4 py-3 text-gray-900 outline-none transition focus:border-gray-400 focus:ring-4 focus:ring-gray-200"
                  placeholder="name@company.com"
                  autoComplete="email"
                  disabled={loading}
                />
              </div>

              <div>
                <div className="flex items-center justify-between">
                  <label
                    htmlFor="password"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Password
                  </label>

                  {/* You can link this to your reset page */}
                </div>

                <div className="mt-2 relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    id="password"
                    value={form.password}
                    onChange={onChange}
                    className="w-full rounded-xl border border-gray-300 bg-white px-4 py-3 pr-24 text-gray-900 outline-none transition focus:border-gray-400 focus:ring-4 focus:ring-gray-200"
                    placeholder="••••••••"
                    autoComplete="current-password"
                    disabled={loading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((s) => !s)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 rounded-lg px-3 py-1.5 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100"
                    disabled={loading}
                  >
                    {showPassword ? "Hide" : "Show"}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full rounded-xl bg-gray-900 text-white py-3 font-semibold shadow-sm transition hover:bg-gray-800 focus:outline-none focus:ring-4 focus:ring-gray-200 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {loading ? "Logging in..." : "Log In"}
              </button>
            </form>
          </div>

          <p className="mt-6 text-center text-xs text-gray-500">
            © {new Date().getFullYear()} vyapakprint. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
